/**
 * Original loan amounts: dollar input and file-upload parsing (pure).
 *
 * Data only comes INTO VALUTTEN here. The tenant selects their own file; it is
 * read in the browser, previewed with per-row validation, then submitted via
 * setLoanOriginalAmounts, which re-validates everything server side. Nothing
 * is ever generated for download.
 *
 * Upload is CSV only. apps/client carries no spreadsheet library, and the
 * repo's SheetJS wrapper (packages/shared-file-processing safeXlsxWrapper) is a
 * server-side module, so an .xlsx must be saved as CSV first.
 */

/** $100M, in cents: the server's ceiling. */
export const MAX_ORIGINAL_AMOUNT_CENTS = 10_000_000_000;
/** The server accepts at most this many entries per call. */
export const MAX_UPLOAD_ROWS = 500;
/** Required column headers (case-insensitive), stated in the UI as text. */
export const REQUIRED_COLUMNS = ['loanId', 'originalAmount'] as const;

export type DollarParse = { ok: true; cents: number } | { ok: false; error: string };

// Whole dollars with optional thousands separators, optional cents (max 2 dp).
const DOLLARS_RE = /^(\d{1,3}(,\d{3})+|\d+)(\.\d{1,2})?$/;

/** "$612,500.50" -> 61250050 cents. More than 2 decimals, zero, negatives and junk are rejected. */
export function parseDollarsToCents(input: string): DollarParse {
  const s = input.trim().replace(/^\$\s*/, '');
  if (!s) return { ok: false, error: 'Enter an amount' };
  if (/^\d[\d,]*\.\d{3,}$/.test(s)) return { ok: false, error: 'At most 2 decimal places' };
  if (!DOLLARS_RE.test(s)) return { ok: false, error: 'Enter a dollar amount, e.g. 612500 or 612,500.50' };
  const cents = Math.round(Number(s.replace(/,/g, '')) * 100);
  if (!Number.isSafeInteger(cents) || cents <= 0) return { ok: false, error: 'Must be more than $0' };
  if (cents > MAX_ORIGINAL_AMOUNT_CENTS) return { ok: false, error: 'Must be $100M or less' };
  return { ok: true, cents };
}

/** Minimal RFC-4180 CSV reader: quoted fields, escaped quotes, CRLF/LF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  const src = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') {
        field += '"';
        i++;
      } else if (c === '"') {
        quoted = false;
      } else {
        field += c;
      }
    } else if (c === '"') {
      quoted = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((f) => f.trim() !== ''));
}

export interface UploadPreviewRow {
  line: number;
  loanId: string;
  rawAmount: string;
  cents: number | null;
  error: string | null;
}

export type UploadPreview = { ok: true; rows: UploadPreviewRow[] } | { ok: false; error: string };

const norm = (h: string) => h.trim().toLowerCase().replace(/[\s_]/g, '');

/** Parse an uploaded CSV into a validated preview (the server re-checks every row). */
export function buildUploadPreview(text: string): UploadPreview {
  const table = parseCsv(text);
  if (table.length === 0) return { ok: false, error: 'The file is empty.' };
  const header = table[0].map(norm);
  const idCol = header.indexOf('loanid');
  const amountCol = header.indexOf('originalamount');
  if (idCol < 0 || amountCol < 0) {
    return { ok: false, error: `The first row must name the columns ${REQUIRED_COLUMNS.join(' and ')}.` };
  }
  const body = table.slice(1);
  if (body.length === 0) return { ok: false, error: 'The file has a header row but no loans.' };
  if (body.length > MAX_UPLOAD_ROWS) {
    return { ok: false, error: `At most ${MAX_UPLOAD_ROWS} loans per file (this one has ${body.length}).` };
  }
  const seen = new Set<string>();
  const rows = body.map((r, i): UploadPreviewRow => {
    const loanId = (r[idCol] ?? '').trim();
    const rawAmount = (r[amountCol] ?? '').trim();
    const line = i + 2;
    if (!loanId) return { line, loanId, rawAmount, cents: null, error: 'Missing loanId' };
    if (loanId.includes('/') || loanId.length > 128) return { line, loanId, rawAmount, cents: null, error: 'Invalid loanId' };
    if (seen.has(loanId)) return { line, loanId, rawAmount, cents: null, error: 'Duplicate loanId in this file' };
    seen.add(loanId);
    const parsed = parseDollarsToCents(rawAmount);
    return parsed.ok
      ? { line, loanId, rawAmount, cents: parsed.cents, error: null }
      : { line, loanId, rawAmount, cents: null, error: parsed.error };
  });
  return { ok: true, rows };
}

/** Plain-English text for the server's per-row rejection codes. */
export const REJECTION_TEXT: Record<string, string> = {
  invalid_loan_id: 'Invalid loan id',
  duplicate_loan_id: 'Duplicate loan id',
  not_integer_cents: 'Invalid amount',
  not_positive: 'Must be more than $0',
  above_cap: 'Must be $100M or less',
  invalid_note: 'Note too long',
  not_found_or_out_of_scope: 'Not one of your active loans',
  original_already_reported: 'Your lender already reported the original amount',
  below_first_balance: 'Lower than the first balance your lender reported',
};
